/**
 * Ableton RECORD quantization - label vocabulary.
 *
 * ⚠️ READ THIS BEFORE TOUCHING EITHER QUANT MODULE ⚠️
 *
 * There are TWO quantization vocabularies in Live and they are NOT
 * interchangeable:
 *
 *   record (9):   0  4  8  8T  8+8T  16  16T  16+16T  32
 *   launch (14):  0  8bars  4bars  2bars  1bar  2  2T  4  4T  8  8T  16  16T  32
 *
 * They share most of their label text, which is exactly what makes them
 * dangerous. Live's own enum index `8` is **1/32** in the record enum and
 * **1/4T** in the launch enum. A mix-up produces a WRONG RESULT rather than
 * an error: nothing throws, nothing logs, the quantization is just wrong and
 * the operator finds out mid-set.
 *
 * This vocabulary lives in its own module, with its own parse function, and
 * `abletonLaunchQuant.ts` holds the other one. That separation is
 * deliberate and load-bearing: reaching for the wrong vocabulary requires an
 * explicit cross-module import rather than an autocomplete slip. Do NOT
 * merge them, do NOT add a shared helper that serves both, and do NOT
 * re-export one from the other. (Pattern adopted from OPBridge, who keep
 * theirs split for the same reason - see their DEC-545.)
 *
 * **We never send enum indices.** OPBridge's DEC-545 locks quantization to
 * cross the wire as a LABEL, so neither side holds an index->meaning table
 * and the collision above cannot happen. The device exposes a parallel
 * `set_rec_quant_enum` arm; we do not touch it.
 */

/** The nine record-quantization labels the OP-AbletonSync device accepts, in
 *  Live's own enum order. Index in this array IS Live's enum value, but that
 *  is documentation only - we transmit the label, never the index. */
export const REC_QUANT_LABELS = [
  '0', '4', '8', '8T', '8+8T', '16', '16T', '16+16T', '32',
] as const;

export type RecQuantLabel = (typeof REC_QUANT_LABELS)[number];

/** Human text for the picker. `'0'` is a REAL value meaning "no record
 *  quantization", NOT the absence of a setting - see `normalizeRecQuant`. */
export const REC_QUANT_DISPLAY: Record<RecQuantLabel, string> = {
  '0': 'None',
  '4': '1/4',
  '8': '1/8',
  '8T': '1/8T',
  '8+8T': '1/8 + 1/8T',
  '16': '1/16',
  '16T': '1/16T',
  '16+16T': '1/16 + 1/16T',
  '32': '1/32',
};

/**
 * Validate + canonicalise a record-quantization label.
 *
 * Returns the canonical label, or `null` when the input is not a record
 * quantization value. Callers MUST check for `null` and skip the send: an
 * unrecognised label is a **silent no-op device-side** (it prints
 * `RX_REC_QUANT_BAD` to the Max Console, which nobody is watching during a
 * show), so validating here is the only thing standing between a typo and a
 * control that quietly does nothing.
 *
 * ⚠️ `'0'` is a REAL label meaning None and normalises successfully.
 * Empty / whitespace / undefined means "leave Ableton's setting alone" and
 * returns `null` so nothing is sent. Never collapse those two cases - sending
 * `'0'` for "unset" would actively turn the operator's quantization off.
 *
 * Tolerates surrounding whitespace and lowercase `t` (`'16t'` -> `'16T'`),
 * because those are the two forms a hand-typed or round-tripped value
 * actually arrives in.
 */
export function normalizeRecQuant(raw: string | null | undefined): RecQuantLabel | null {
  if (raw == null) return null;
  const trimmed = raw.trim().replace(/"/g, '');
  if (trimmed === '') return null;
  const upper = trimmed.toUpperCase();
  return (REC_QUANT_LABELS as readonly string[]).includes(upper)
    ? (upper as RecQuantLabel)
    : null;
}

/**
 * Render a record-quantization label for display.
 *
 * `null` / `undefined` means "we have not heard from the device" and renders
 * as `'--'`, NEVER as `'None'`. Those are different facts: `'None'` is Live
 * telling us quantization is off, `'--'` is us not knowing. Rendering absent
 * as None is a wrong answer wearing the costume of a real one.
 */
export function displayRecQuant(label: string | null | undefined): string {
  const norm = normalizeRecQuant(label);
  return norm === null ? '--' : REC_QUANT_DISPLAY[norm];
}
